<script lang="ts">
  import {
    Alignment,
    CSS_ALIGNMENT,
    CSS_SIZE,
    Size,
  } from "$components/editor/extensions/blocks/shared/attributes";
  import HoverMenu from "$components/editor/extensions/blocks/HoverMenu/HoverMenu.svelte";
  import GutterMenu from "../+shared/GutterMenu.svelte";
  import {
    createFigmaEmbedURL,
    createLoomEmbedURL,
    createVimeoEmbedURL,
    EmbedProvider,
    getEmbedURLFromYoutubeURL,
  } from "./utils";
  import { NodeViewWrapper } from "svelte-tiptap";
  import type { NodeViewProps } from "@tiptap/core";

  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let node: NodeViewProps["node"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let selected: NodeViewProps["selected"];
  export let isEditable: boolean;
  $$restProps; // prevent svelte unused props warning

  let loaded = false;
  let error = false;

  const changeSize = (width: number | string) => {
    updateAttributes({
      width,
      height: "100%",
    });
  };
  const setAlign = (align: string) => {
    updateAttributes({
      align,
    });
  };

  $: attrs = node.attrs as {
    align: Alignment;
    width: Size;
    src: string;
    match: RegExpMatchArray;
    type: EmbedProvider;
    inline: boolean;
    height: number | string;
    controls: boolean;
    nocookie: boolean;
    allowFullscreen: boolean;
    HTMLAttributes: Record<string, any>;
    autoplay: boolean;
    init: boolean;
    start: number;
  };

  $: ({ align, width } = node.attrs);

  let embedURL = "";
  $: try {
    if (!attrs.src) error = true;

    if (attrs.type == EmbedProvider.figma) {
      embedURL = createFigmaEmbedURL(attrs.src);
    } else if (attrs.type === EmbedProvider.loom) {
      embedURL = createLoomEmbedURL(attrs.match[3]);
    } else if (attrs.type === EmbedProvider.vimeo) {
      embedURL = createVimeoEmbedURL(attrs.match[5]);
    } else if (
      [EmbedProvider.graphy, EmbedProvider.codesandbox, EmbedProvider.rive].includes(attrs.type)
    ) {
      embedURL = attrs.src;
    } else if (attrs.type === EmbedProvider.youtube) {
      embedURL = getEmbedURLFromYoutubeURL({
        url: attrs.src,
        controls: attrs.controls,
        nocookie: attrs.nocookie,
        startAt: attrs.start || 0,
        autoplay: attrs.autoplay,
      });
    }
  } catch (e) {
    console.log(e);
    error = true;
  }
</script>

<NodeViewWrapper class="iframe not-prose group relative rounded">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
  {/if}

  {#if error}
    {#if isEditable}
      <div class="text-white">Failed to load url {embedURL}</div>
    {/if}
  {:else}
    <div class="flex flex-row {CSS_ALIGNMENT[attrs.align]}">
      <!-- { Loading placeholder } -->
      {#if !loaded}
        <div
          class:ring-4={isEditable && selected}
          class="aspect-video animate-pulse rounded bg-white/10 {CSS_SIZE[attrs.width]}"
          class:aspect-square={attrs.type === EmbedProvider.figma}
        />
      {/if}
      <!-- { Iframe with menu } -->
      <iframe
        src={embedURL}
        title="an embed"
        height="100%"
        class:ring-4={isEditable && selected}
        allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        class="rounded-lg {CSS_SIZE[attrs.width]} bg-black/3 dark:bg-white/2"
        class:hidden={!loaded}
        class:aspect-video={[
          EmbedProvider.youtube,
          EmbedProvider.loom,
          EmbedProvider.vimeo,
          EmbedProvider.graphy,
          EmbedProvider.rive,
        ].includes(attrs.type)}
        class:aspect-square={[EmbedProvider.figma, EmbedProvider.codesandbox].includes(attrs.type)}
        on:load={() => (loaded = true)}
      />

      {#if isEditable}
        <div
          class="absolute inset-x-0 bottom-0 z-[999] mb-2 hidden translate-y-full justify-center group-hover:flex"
        >
          <HoverMenu
            {align}
            {width}
            on:sizeChange={(e) => changeSize(e.detail)}
            on:alignChange={(e) => setAlign(e.detail)}
          />
        </div>
      {/if}
    </div>
  {/if}
</NodeViewWrapper>
